import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ministeriosAPI, personasAPI } from "@/lib/api";
import { ArrowLeft, UserPlus, Users, Trash2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function MinisterioMiembrosPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ministerio, setMinisterio] = useState(null);
  const [todasPersonas, setTodasPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [removeId, setRemoveId] = useState(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [ministerioRes, personasRes] = await Promise.all([
        ministeriosAPI.getMiembros(id),
        personasAPI.getAll(),
      ]);
      setMinisterio(ministerioRes.data);
      setTodasPersonas(personasRes.data);
    } catch (error) {
      toast.error("Error al cargar los datos");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMiembro = async (personaId) => {
    try {
      await ministeriosAPI.addMiembro(id, personaId);
      toast.success("Miembro agregado exitosamente");
      loadData();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Error al agregar miembro"
      );
    }
  };

  const handleRemoveMiembro = async () => {
    try {
      await ministeriosAPI.removeMiembro(id, removeId);
      toast.success("Miembro eliminado exitosamente");
      setRemoveId(null);
      loadData();
    } catch (error) {
      toast.error("Error al eliminar miembro");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-stone-400">Cargando...</div>
      </div>
    );
  }

  if (!ministerio) {
    return (
      <div className="text-center py-8">
        <p className="text-stone-500">Ministerio no encontrado</p>
        <Button onClick={() => navigate("/ministerios")} className="mt-4">
          Volver a Ministerios
        </Button>
      </div>
    );
  }

  const miembrosIds = ministerio.miembros?.map((m) => m.id) || [];
  const personasDisponibles = todasPersonas
    .filter((p) => !miembrosIds.includes(p.id))
    .filter(
      (p) =>
        p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.apellido.toLowerCase().includes(searchTerm.toLowerCase())
    );

  return (
    <div className="space-y-6" data-testid="ministerio-miembros-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/ministerios")}
          className="shrink-0"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-serif text-rose-950 tracking-tight">
            {ministerio.nombre}
          </h1>
          <p className="text-stone-600 mt-1">Gestión de miembros</p>
        </div>
        <Badge variant="outline" className="shrink-0">
          {ministerio.miembros?.length || 0} miembros
        </Badge>
      </div>

      {/* Descripción */}
      {ministerio.descripcion && (
        <Card className="border-stone-200 bg-stone-50">
          <CardContent className="p-4">
            <p className="text-sm text-stone-600">{ministerio.descripcion}</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Miembros Actuales */}
        <Card className="border-stone-200 bg-white">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl font-serif text-stone-800 flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              Miembros Actuales
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!ministerio.miembros || ministerio.miembros.length === 0 ? (
              <div className="text-center py-8 text-stone-500">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No hay miembros en este ministerio</p>
              </div>
            ) : (
              <div className="space-y-3">
                {ministerio.miembros.map((persona) => (
                  <div
                    key={persona.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-stone-50 border border-stone-100"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-stone-800 truncate">
                        {persona.nombre} {persona.apellido}
                      </p>
                      <p className="text-xs text-stone-500 capitalize">
                        {persona.tipo}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50 shrink-0"
                      onClick={() => setRemoveId(persona.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Agregar Miembros */}
        <Card className="border-stone-200 bg-white">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl font-serif text-stone-800 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-600" />
              Agregar Miembros
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                placeholder="Buscar por nombre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Lista de personas disponibles */}
            <div className="max-h-96 overflow-y-auto space-y-2">
              {personasDisponibles.length === 0 ? (
                <div className="text-center py-8 text-stone-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">
                    {searchTerm
                      ? "No se encontraron personas"
                      : "Todas las personas ya son miembros"}
                  </p>
                </div>
              ) : (
                personasDisponibles.map((persona) => (
                  <div
                    key={persona.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-stone-50 border border-stone-100 hover:bg-stone-100 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-stone-800 truncate">
                        {persona.nombre} {persona.apellido}
                      </p>
                      <p className="text-xs text-stone-500 capitalize">
                        {persona.tipo} · {persona.genero}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleAddMiembro(persona.id)}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                    >
                      <UserPlus className="w-4 h-4 mr-1" />
                      Agregar
                    </Button>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={!!removeId} onOpenChange={() => setRemoveId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este miembro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta persona será eliminada del ministerio. Puedes agregarla
              nuevamente en cualquier momento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMiembro}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
